// libraries
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button, notification, Tabs, Tooltip, Typography } from 'antd';
import { EditFilled, LoadingOutlined } from '@ant-design/icons';

// components
import { WordTrackListContentDesktop } from './word-track-list-content.desktop';
import { WordTrackListContentMobile } from './word-track-list-content.mobile';
import { WordTrackListDrawer } from './word-track-list-drawer';

// services
import * as WordTrackService from '../../../services/bb-api/word-track';

// utils
import { txt } from '../../../locales/i18n';
import { useHistory, useLocation } from 'react-router-dom';
import { useDeviceSize } from '../../../hooks/device-size';

export function WordTrackTable(): JSX.Element {
	const [totalMatches, setTotalMatches] = useState<number>(0);
	const [showDrawer, setShowDrawer] = useState<boolean>(false);
	const [words, setWords] = useState<Record<string, any>[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [wordTracks, setWordTracks] = useState<Record<string, any>[]>([]);
	const [activeTab, setActiveTab] = useState<string>('');
	const isMobileDevice = useDeviceSize();

	const location = useLocation();
	const history = useHistory();

	const getWords = useCallback(async () => {
		const words: Record<string, any>[] = await WordTrackService.getWords();
		setWords(words);
		return words;
	}, []);

	const getWordTracks = useCallback(async () => {
		setLoading(true);

		try {
			const wordTrackMessages: Record<string, any>[] = await WordTrackService.getMessages();
			const words: Record<string, any>[] = await getWords();

			const [entryWordTrack]: Record<string, any>[] = wordTrackMessages;
			setTotalMatches(entryWordTrack?.totalMatches ?? 0);

			const wordMatcherString: string[] = words.map(({ word }) => {
				return word.replace(/^%|%$/gm, '[\\wæøåÆØÅ]*');
			});

			const matcherString: string = `(?:[^\\wæøåÆØÅ])(${wordMatcherString.join('|')})(?![\\wæøåÆØÅ])`;

			wordTrackMessages.forEach((wordTrack: Record<string, any>) => {
				const matcher: RegExp = new RegExp(matcherString, 'gmi');
				wordTrack.words = new Set();

				wordTrack.body = (' ' + wordTrack.body).replace(matcher, (match: string) => {
					const [suffix, ...word]: string[] = match.split('');
					wordTrack.words.add(word.join(''));
					return `${suffix}<span class="word-highlight">${word.join('')}</span>`;
				});

				wordTrack.body = wordTrack.body.substring(1);
			});

			setWordTracks(wordTrackMessages);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
		setActiveTab(location.hash);
	}, [location.hash, getWords]);

	useEffect(() => {
		getWordTracks();
	}, [getWordTracks]);

	const tabBarExtraContent = {
		left: (
			<Typography.Text type="secondary">
				{loading ? <LoadingOutlined /> : txt('global.table query notification', { amount: totalMatches })}
			</Typography.Text>
		),

		right: (
			<Tooltip title="update word list">
				<Button type="primary" onClick={() => setShowDrawer(true)}>
					<EditFilled />
				</Button>
			</Tooltip>
		),
	};

	return (
		<StyledMain>
			<Tabs
				centered
				activeKey={activeTab}
				tabBarExtraContent={tabBarExtraContent}
				onChange={(activeTab: string) => history.push({ hash: activeTab })}
			>
				<Tabs.TabPane tab="Boblberg" key="">
					{isMobileDevice === false && (
						<WordTrackListContentDesktop
							content={wordTracks}
							updateTableContent={getWordTracks}
							useLoading={[loading, setLoading]}
						/>
					)}

					{isMobileDevice === true && (
						<WordTrackListContentMobile
							content={wordTracks}
							updateTableContent={getWordTracks}
							useLoading={[loading, setLoading]}
						/>
					)}
				</Tabs.TabPane>

				<Tabs.TabPane tab={txt('menu.groups')} key="#groups">
					{isMobileDevice === false && (
						<WordTrackListContentDesktop
							content={wordTracks}
							updateTableContent={getWordTracks}
							useLoading={[loading, setLoading]}
						/>
					)}

					{isMobileDevice === true && (
						<WordTrackListContentMobile
							content={wordTracks}
							updateTableContent={getWordTracks}
							useLoading={[loading, setLoading]}
						/>
					)}
				</Tabs.TabPane>
			</Tabs>

			<WordTrackListDrawer words={words} getWords={getWords} useVisible={[showDrawer, setShowDrawer]} />
		</StyledMain>
	);
}

const StyledMain = styled.main`
	display: flex;
	flex-direction: column;
	position: relative;

	.ant-tabs-nav {
		margin: 0rem;
		padding-inline: 2rem;
	}

	.ant-tabs-content-holder {
		overflow-y: auto;
	}
`;
